import React, { useState, useEffect, useRef } from 'react';
import { Plus, ChevronDown, Check, X, Search, Building2, FileText, Upload, Activity, ShieldCheck } from 'lucide-react';
import DoctorService from './DoctorService';

const DoctorForm = ({ formData, handleFormChange, handleFileChange, handleSubmit, onCancel, editMode, subscriptionPanel }) => {
  const [uploading, setUploading] = useState({
    vendor_profile_path: false,
    license: false,
    authorizedSignature: false,
    commercialLicense: false,
    civilId: false,
  });
  const [clinics, setClinics] = useState([]);
  const [specialities, setSpecialities] = useState([]);
  const [loadingClinics, setLoadingClinics] = useState(true);
  const [loadingSpecialities, setLoadingSpecialities] = useState(true);
  const [error, setError] = useState(null);

  // Clinic multi-select dropdown state
  const [isClinicOpen, setIsClinicOpen] = useState(false);
  const [clinicSearch, setClinicSearch] = useState('');
  const clinicDropdownRef = useRef(null);

  // Conditions & Procedures input state
  const [conditionInput, setConditionInput] = useState('');
  const [procedureInput, setProcedureInput] = useState('');

  const currentConditions = Array.isArray(formData.conditions) ? formData.conditions : [];
  const currentProcedures = Array.isArray(formData.procedures) ? formData.procedures : [];

  const handleAddCondition = (e) => {
    if (e) e.preventDefault();
    const val = conditionInput.trim();
    if (!val) return;
    if (!currentConditions.includes(val)) {
      handleFormChange('conditions', [...currentConditions, val]);
    }
    setConditionInput('');
  };

  const handleRemoveCondition = (indexToRemove) => {
    const updated = currentConditions.filter((_, idx) => idx !== indexToRemove);
    handleFormChange('conditions', updated);
  };

  const handleAddProcedure = (e) => {
    if (e) e.preventDefault();
    const val = procedureInput.trim();
    if (!val) return;
    if (!currentProcedures.includes(val)) {
      handleFormChange('procedures', [...currentProcedures, val]);
    }
    setProcedureInput('');
  };

  const handleRemoveProcedure = (indexToRemove) => {
    const updated = currentProcedures.filter((_, idx) => idx !== indexToRemove);
    handleFormChange('procedures', updated);
  };

  useEffect(() => {
    const fetchClinics = async () => {
      try {
        setLoadingClinics(true);
        const response = await DoctorService.getClinicList();
        if (response.msg !== 'Success') {
          throw new Error('Failed to fetch clinics');
        }
        setClinics(Array.isArray(response.data) ? response.data : []);
      } catch (error) {
        setError(`Error fetching clinics: ${error.message}`);
      } finally {
        setLoadingClinics(false);
      }
    };

    const fetchSpecialities = async () => {
      try {
        setLoadingSpecialities(true);
        const response = await DoctorService.getSpecialityList();
        if (response.msg !== 'Success') {
          throw new Error('Failed to fetch specialities');
        }
        setSpecialities(Array.isArray(response.data) ? response.data : []);
      } catch (error) {
        setError(`Error fetching specialities: ${error.message}`);
      } finally {
        setLoadingSpecialities(false);
      }
    };

    fetchClinics();
    fetchSpecialities();
  }, []);

  // Handle click outside clinic dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (clinicDropdownRef.current && !clinicDropdownRef.current.contains(event.target)) {
        setIsClinicOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Selected clinic IDs array
  const selectedClinicIds = Array.isArray(formData.clinic_ids) && formData.clinic_ids.length > 0
    ? formData.clinic_ids.map(Number).filter(Boolean)
    : formData.clinic_id
      ? [parseInt(formData.clinic_id)].filter(Boolean)
      : [];

  const handleToggleClinic = (clinicId) => {
    const cId = Number(clinicId);
    let updated;
    if (selectedClinicIds.includes(cId)) {
      updated = selectedClinicIds.filter((id) => id !== cId);
    } else {
      updated = [...selectedClinicIds, cId];
    }
    handleFormChange('clinic_ids', updated);
    handleFormChange('clinic_id', updated[0] || '');
  };

  const handleSelectAllClinics = () => {
    const allIds = clinics.map((c) => Number(c.clinicId)).filter(Boolean);
    handleFormChange('clinic_ids', allIds);
    handleFormChange('clinic_id', allIds[0] || '');
  };

  const handleClearAllClinics = () => {
    handleFormChange('clinic_ids', []);
    handleFormChange('clinic_id', '');
  };

  const filteredClinics = clinics.filter((c) =>
    (c.clinic || '').toLowerCase().includes(clinicSearch.toLowerCase())
  );

  const handleFileUpload = async (event, field) => {
    const file = event.target.files[0];
    if (!file) return;

    setUploading((prev) => ({ ...prev, [field]: true }));
    try {
      const response = await DoctorService.uploadProfilePhoto(file);
      handleFileChange(field, { target: { files: [file] } });
      handleFormChange(field, response.privatePathUrl); // Store the URL
    } catch (error) {
      alert(`Error uploading ${field}: ${error.message}`);
    } finally {
      setUploading((prev) => ({ ...prev, [field]: false }));
    }
  };

  return (
    <div className="form-view">
      <div className="form-header">
        <button className="back-btn" onClick={onCancel}>
          ← Back
        </button>
        <h2>{editMode ? 'Edit Doctor' : 'Create New Doctor'}</h2>
      </div>
      <div className="form-content">
        {error && <div className="error-message text-red-500 mb-4">{error}</div>}
        <div className="form-grid">
          <div className="form-item">
            <label className="mandatory">Profile Photo</label>
            <div className="relative w-32 h-32 mx-auto">
              <div className="w-full h-full rounded-full bg-gray-200 flex items-center justify-center overflow-hidden">
                {formData.vendor_profile_path ? (
                  <img
                    src={
                      typeof formData.vendor_profile_path === 'string'
                        ? formData.vendor_profile_path
                        : URL.createObjectURL(formData.vendor_profile_path)
                    }
                    alt="Profile Preview"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <span className="text-gray-500">No Image</span>
                )}
              </div>
              <label className="absolute bottom-0 right-0 bg-blue-500 rounded-full p-2 cursor-pointer hover:bg-blue-600 transition-colors">
                <Plus size={20} className="text-white" />
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleFileUpload(e, 'vendor_profile_path')}
                  className="hidden"
                  disabled={uploading.vendor_profile_path}
                  required={!editMode}
                />
              </label>
            </div>
            {uploading.vendor_profile_path && <span className="text-sm text-gray-500">Uploading...</span>}
          </div>

          <div className="form-item">
            <label>Title</label>
            <select
              value={formData.titleType || 'Dr.'}
              onChange={(e) => handleFormChange('titleType', e.target.value)}
            >
              <option value="Dr.">Dr.</option>
              <option value="Prof.">Prof.</option>
              <option value="Assoc. Prof.">Assoc. Prof.</option>
              <option value="Mr.">Mr.</option>
              <option value="Ms.">Ms.</option>
            </select>
          </div>

          <div className="form-item">
            <label className="mandatory">Doctor Name</label>
            <input
              type="text"
              value={formData.vendorName || ''}
              onChange={(e) => handleFormChange('vendorName', e.target.value)}
              placeholder="Enter doctor name"
              required
            />
          </div>

          <div className="form-item">
            <label className="mandatory">Doctor Email</label>
            <input
              type="email"
              value={formData.vendorEmail || ''}
              onChange={(e) => handleFormChange('vendorEmail', e.target.value)}
              placeholder="Enter email"
              required
            />
          </div>

          {!editMode && (
            <div className="form-item">
              <label className="mandatory">Password</label>
              <input
                type="text"
                value={formData.password || ''}
                onChange={(e) => handleFormChange('password', e.target.value)}
                placeholder="Enter password"
                required
              />
            </div>
          )}

          <div className="form-item">
            <label className="mandatory">Mobile Number</label>
            <input
              type="tel"
              value={formData.vendorConNumber || ''}
              onChange={(e) => handleFormChange('vendorConNumber', e.target.value)}
              placeholder="Enter mobile number"
              required
              maxLength={10}
            />
          </div>

          <div className="form-item">
            <label className="mandatory">Speciality</label>
            <select
              value={formData.specialty_id || ''}
              onChange={(e) => handleFormChange('specialty_id', e.target.value)}
              required
              disabled={loadingSpecialities}
            >
              <option value="">Select a Speciality</option>
              {specialities.map((speciality) => (
                <option key={speciality.specialityId} value={speciality.specialityId}>
                  {speciality.speciality}
                </option>
              ))}
            </select>
            {loadingSpecialities && <span className="text-sm text-gray-500">Loading Specialities...</span>}
          </div>

          <div className="form-item">
            <label className="mandatory">Education</label>
            <input
              type="text"
              value={formData.education || ''}
              onChange={(e) => handleFormChange('education', e.target.value)}
              placeholder="Enter education (e.g. MBBS, MD)"
              required
            />
          </div>

          <div className="form-item">
            <label>Experience (Years)</label>
            <input
              type="text"
              value={formData.experience || ''}
              onChange={(e) => handleFormChange('experience', e.target.value)}
              placeholder="e.g. 5 years or 8"
            />
          </div>

          <div className="form-item">
            <label>Gender</label>
            <select
              value={formData.gender || ''}
              onChange={(e) => handleFormChange('gender', e.target.value)}
            >
              <option value="">Select gender</option>
              <option value="Male">Male</option>
              <option value="Female">Female</option>
              <option value="Other">Other</option>
            </select>
          </div>

          <div className="form-item">
            <label>Registration Number</label>
            <input
              type="text"
              value={formData.regNumber || ''}
              onChange={(e) => handleFormChange('regNumber', e.target.value)}
              placeholder="Enter medical registration number"
            />
          </div>

          <div className="form-item">
            <label>Location / Area</label>
            <input
              type="text"
              value={formData.location || ''}
              onChange={(e) => handleFormChange('location', e.target.value)}
              placeholder="e.g. Indiranagar, Bangalore"
            />
          </div>

          <div className="form-item">
            <label>Practice / Clinic Address</label>
            <input
              type="text"
              value={formData.address || ''}
              onChange={(e) => handleFormChange('address', e.target.value)}
              placeholder="Enter full practice address"
            />
          </div>

          <div className="form-item">
            <label>Languages Spoken</label>
            <input
              type="text"
              value={formData.languages || ''}
              onChange={(e) => handleFormChange('languages', e.target.value)}
              placeholder="e.g. English, Hindi, Tamil"
            />
          </div>

          {/* Multi-Clinic Selection Component */}
          <div className="form-item relative" ref={clinicDropdownRef}>
            <div className="flex items-center justify-between mb-1">
              <label className="mandatory flex items-center gap-1 font-semibold text-slate-700">
                <Building2 size={15} className="text-teal-600" />
                Clinics <span className="text-xs text-slate-500 font-normal">(Multiple Select)</span>
              </label>
              {selectedClinicIds.length > 0 && (
                <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-teal-50 text-teal-700 border border-teal-200">
                  {selectedClinicIds.length} Selected
                </span>
              )}
            </div>

            {/* Selected Clinic Chips */}
            {selectedClinicIds.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mb-2 max-h-28 overflow-y-auto p-2 bg-slate-50 rounded-lg border border-slate-200">
                {selectedClinicIds.map((cId) => {
                  const clinicObj = clinics.find((c) => Number(c.clinicId) === Number(cId));
                  return (
                    <span
                      key={cId}
                      className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-teal-600 text-white shadow-sm transition-all"
                    >
                      {clinicObj ? clinicObj.clinic : `Clinic ${cId}`}
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleToggleClinic(cId);
                        }}
                        className="hover:bg-teal-700 rounded-full p-0.5 transition-colors"
                        title="Remove clinic"
                      >
                        <X size={12} />
                      </button>
                    </span>
                  );
                })}
              </div>
            )}

            {/* Dropdown Trigger Box */}
            <div
              onClick={() => !loadingClinics && setIsClinicOpen((prev) => !prev)}
              className={`w-full min-h-[44px] px-3 py-2 bg-white border rounded-lg cursor-pointer flex items-center justify-between transition-all ${
                isClinicOpen
                  ? 'border-teal-500 ring-2 ring-teal-100'
                  : selectedClinicIds.length === 0
                    ? 'border-rose-300'
                    : 'border-slate-300 hover:border-slate-400'
              }`}
            >
              <span className={`text-sm ${selectedClinicIds.length > 0 ? 'text-slate-800 font-medium' : 'text-slate-400'}`}>
                {loadingClinics
                  ? 'Loading clinics...'
                  : selectedClinicIds.length > 0
                    ? `${selectedClinicIds.length} clinic${selectedClinicIds.length > 1 ? 's' : ''} selected`
                    : 'Select clinics (Multiple) *'}
              </span>
              <ChevronDown
                size={18}
                className={`text-slate-400 transition-transform ${isClinicOpen ? 'rotate-180 text-teal-600' : ''}`}
              />
            </div>

            {/* Dropdown Menu */}
            {isClinicOpen && (
              <div className="absolute left-0 right-0 top-full mt-1.5 bg-white border border-slate-200 rounded-xl shadow-xl z-50 overflow-hidden animate-in fade-in slide-in-from-top-2 duration-150">
                {/* Search and Quick Selection Actions */}
                <div className="p-2.5 border-b border-slate-100 bg-slate-50">
                  <div className="relative mb-2">
                    <Search size={14} className="absolute left-2.5 top-2.5 text-slate-400" />
                    <input
                      type="text"
                      placeholder="Search clinics..."
                      value={clinicSearch}
                      onChange={(e) => setClinicSearch(e.target.value)}
                      className="w-full pl-8 pr-3 py-1.5 text-xs bg-white border border-slate-200 rounded-lg focus:outline-none focus:border-teal-500"
                      onClick={(e) => e.stopPropagation()}
                    />
                  </div>
                  <div className="flex items-center justify-between text-xs px-1">
                    <button
                      type="button"
                      onClick={handleSelectAllClinics}
                      className="text-teal-700 hover:text-teal-800 font-medium hover:underline"
                    >
                      Select All ({clinics.length})
                    </button>
                    <button
                      type="button"
                      onClick={handleClearAllClinics}
                      className="text-slate-500 hover:text-rose-600 font-medium hover:underline"
                    >
                      Clear All
                    </button>
                  </div>
                </div>

                {/* Clinics List with Checkboxes */}
                <div className="max-h-60 overflow-y-auto p-1.5 divide-y divide-slate-50">
                  {filteredClinics.length === 0 ? (
                    <div className="p-3 text-center text-xs text-slate-400">
                      No clinics found
                    </div>
                  ) : (
                    filteredClinics.map((clinic) => {
                      const isSelected = selectedClinicIds.includes(Number(clinic.clinicId));
                      return (
                        <div
                          key={clinic.clinicId}
                          onClick={() => handleToggleClinic(clinic.clinicId)}
                          className={`flex items-center gap-2.5 px-3 py-2 rounded-lg cursor-pointer text-sm transition-colors ${
                            isSelected ? 'bg-teal-50 text-teal-900 font-medium' : 'hover:bg-slate-50 text-slate-700'
                          }`}
                        >
                          <div
                            className={`w-4 h-4 rounded flex items-center justify-center border transition-all ${
                              isSelected ? 'bg-teal-600 border-teal-600 text-white' : 'border-slate-300 bg-white'
                            }`}
                          >
                            {isSelected && <Check size={12} strokeWidth={3} />}
                          </div>
                          <span className="flex-1 truncate">{clinic.clinic}</span>
                        </div>
                      );
                    })
                  )}
                </div>

                <div className="p-2 border-t border-slate-100 bg-slate-50 text-right">
                  <button
                    type="button"
                    onClick={() => setIsClinicOpen(false)}
                    className="px-4 py-1.5 bg-teal-600 hover:bg-teal-700 text-white text-xs font-semibold rounded-lg shadow-sm"
                  >
                    Done
                  </button>
                </div>
              </div>
            )}

            {selectedClinicIds.length === 0 && (
              <span className="text-xs text-rose-500 mt-1 block">* At least one clinic is required</span>
            )}
          </div>

          {/* ── Doctor Commitment – Assured Appointment Section ───────────────────────────── */}
          <div className="form-item col-span-full border-t border-slate-200 pt-4 mt-2">
            <div className="flex items-center justify-between mb-1.5">
              <h3 className="text-sm font-bold text-slate-800 flex items-center gap-2 m-0">
                <ShieldCheck size={17} className="text-teal-600" />
                Doctor Commitment – Assured Appointment
              </h3>
              <span className={`text-xs px-2.5 py-0.5 rounded-full font-bold border ${
                formData.commitment_type === 'committed' || formData.is_committed === 1 || formData.is_committed === '1'
                  ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                  : 'bg-slate-100 text-slate-600 border-slate-200'
              }`}>
                {formData.commitment_type === 'committed' || formData.is_committed === 1 || formData.is_committed === '1'
                  ? 'Committed Doctor (Active)'
                  : 'Normal Doctor'}
              </span>
            </div>
            <p className="text-xs text-slate-500 mb-3">
              Configure whether this doctor participates in the Assured Appointment commitment on the patient portal.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3.5">
              {/* Option 1: Normal Doctor */}
              <div
                onClick={() => {
                  handleFormChange('commitment_type', 'normal');
                  handleFormChange('is_committed', 0);
                }}
                className={`p-3.5 rounded-xl border-2 cursor-pointer transition-all flex flex-col justify-between ${
                  formData.commitment_type !== 'committed' && formData.is_committed !== 1 && formData.is_committed !== '1'
                    ? 'border-teal-500 bg-teal-50/40 shadow-xs'
                    : 'border-slate-200 bg-white hover:border-slate-300'
                }`}
              >
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-xs font-bold text-slate-800 flex items-center gap-1.5">
                      Normal Doctor
                    </span>
                    <input
                      type="radio"
                      name="commitment_type"
                      checked={formData.commitment_type !== 'committed' && formData.is_committed !== 1 && formData.is_committed !== '1'}
                      onChange={() => {
                        handleFormChange('commitment_type', 'normal');
                        handleFormChange('is_committed', 0);
                      }}
                      className="accent-teal-600 cursor-pointer"
                    />
                  </div>
                  <p className="text-xs text-slate-500 m-0 leading-relaxed">
                    Regular appointment booking without additional time or waiting-time commitments.
                  </p>
                </div>
                <div className="mt-3 pt-2 border-t border-slate-100 text-[11px] text-slate-400">
                  Standard scheduling policy applies.
                </div>
              </div>

              {/* Option 2: Committed Doctor */}
              <div
                onClick={() => {
                  handleFormChange('commitment_type', 'committed');
                  handleFormChange('is_committed', 1);
                }}
                className={`p-3.5 rounded-xl border-2 cursor-pointer transition-all flex flex-col justify-between ${
                  formData.commitment_type === 'committed' || formData.is_committed === 1 || formData.is_committed === '1'
                    ? 'border-emerald-500 bg-emerald-50/40 shadow-xs'
                    : 'border-slate-200 bg-white hover:border-slate-300'
                }`}
              >
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-xs font-bold text-emerald-900 flex items-center gap-1.5">
                      <ShieldCheck size={15} className="text-emerald-600" />
                      Committed Doctor (Assured Appointment)
                    </span>
                    <input
                      type="radio"
                      name="commitment_type"
                      checked={formData.commitment_type === 'committed' || formData.is_committed === 1 || formData.is_committed === '1'}
                      onChange={() => {
                        handleFormChange('commitment_type', 'committed');
                        handleFormChange('is_committed', 1);
                      }}
                      className="accent-emerald-600 cursor-pointer"
                    />
                  </div>
                  <p className="text-xs text-slate-600 m-0 mb-2.5 leading-relaxed font-medium">
                    Doctor voluntarily agrees to appointment timing and waiting-time commitments.
                  </p>
                  <div className="space-y-1.5 text-[11px] text-slate-700 bg-white/80 p-2.5 rounded-lg border border-emerald-100">
                    <div className="flex items-start gap-1.5">
                      <span className="text-emerald-600 font-bold">✓</span>
                      <span><strong>Advance Payment:</strong> Patient prepays to confirm appointment.</span>
                    </div>
                    <div className="flex items-start gap-1.5">
                      <span className="text-emerald-600 font-bold">✓</span>
                      <span><strong>Automatic Refund:</strong> Processed if doctor fails agreed commitment.</span>
                    </div>
                    <div className="flex items-start gap-1.5">
                      <span className="text-emerald-600 font-bold">✓</span>
                      <span><strong>Special Badge:</strong> Displays "Preferred Doctor / Assured Doctor" on patient app.</span>
                    </div>
                    <div className="flex items-start gap-1.5">
                      <span className="text-emerald-600 font-bold">✓</span>
                      <span><strong>Search Priority:</strong> Receives priority visibility in patient search.</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* ── Conditions & Procedures Section ───────────────────────────── */}
          <div className="form-item col-span-full border-t border-slate-200 pt-4 mt-2">
            <h3 className="text-sm font-bold text-slate-800 mb-3 flex items-center gap-2">
              <Activity size={16} className="text-teal-600" />
              Conditions Treated & Procedures Performed
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Conditions Treated */}
              <div className="p-3 bg-slate-50 border border-slate-200 rounded-xl flex flex-col gap-2.5">
                <div className="flex items-center justify-between">
                  <label className="text-xs font-semibold text-slate-700">Top Conditions Treated</label>
                  <span className="text-[11px] font-semibold text-teal-700 bg-teal-50 px-2 py-0.5 rounded-full border border-teal-200">
                    {currentConditions.length} added
                  </span>
                </div>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={conditionInput}
                    onChange={(e) => setConditionInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAddCondition();
                      }
                    }}
                    placeholder="Type condition & press Enter or click Add"
                    className="flex-1 text-xs px-3 py-2 bg-white border border-slate-200 rounded-lg focus:outline-none focus:border-teal-500"
                  />
                  <button
                    type="button"
                    onClick={handleAddCondition}
                    className="px-3 py-1.5 bg-teal-600 hover:bg-teal-700 text-white text-xs font-semibold rounded-lg shadow-sm flex items-center gap-1 transition-all cursor-pointer"
                  >
                    <Plus size={14} /> Add
                  </button>
                </div>
                <div className="flex flex-wrap gap-1.5 min-h-[38px] p-2 bg-white rounded-lg border border-slate-200 max-h-36 overflow-y-auto">
                  {currentConditions.length === 0 ? (
                    <span className="text-xs text-slate-400 italic">No conditions added yet (e.g. Diabetes, Hypertension, Asthma)</span>
                  ) : (
                    currentConditions.map((cond, idx) => (
                      <span
                        key={idx}
                        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-teal-50 text-teal-800 border border-teal-200 shadow-2xs"
                      >
                        {cond}
                        <button
                          type="button"
                          onClick={() => handleRemoveCondition(idx)}
                          className="hover:bg-teal-200 text-teal-600 hover:text-teal-900 rounded-full p-0.5 transition-colors cursor-pointer border-none bg-transparent"
                          title="Remove condition"
                        >
                          <X size={12} />
                        </button>
                      </span>
                    ))
                  )}
                </div>
              </div>

              {/* Procedures Performed */}
              <div className="p-3 bg-slate-50 border border-slate-200 rounded-xl flex flex-col gap-2.5">
                <div className="flex items-center justify-between">
                  <label className="text-xs font-semibold text-slate-700">Procedures Performed</label>
                  <span className="text-[11px] font-semibold text-indigo-700 bg-indigo-50 px-2 py-0.5 rounded-full border border-indigo-200">
                    {currentProcedures.length} added
                  </span>
                </div>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={procedureInput}
                    onChange={(e) => setProcedureInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAddProcedure();
                      }
                    }}
                    placeholder="Type procedure & press Enter or click Add"
                    className="flex-1 text-xs px-3 py-2 bg-white border border-slate-200 rounded-lg focus:outline-none focus:border-teal-500"
                  />
                  <button
                    type="button"
                    onClick={handleAddProcedure}
                    className="px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-semibold rounded-lg shadow-sm flex items-center gap-1 transition-all cursor-pointer"
                  >
                    <Plus size={14} /> Add
                  </button>
                </div>
                <div className="flex flex-wrap gap-1.5 min-h-[38px] p-2 bg-white rounded-lg border border-slate-200 max-h-36 overflow-y-auto">
                  {currentProcedures.length === 0 ? (
                    <span className="text-xs text-slate-400 italic">No procedures added yet (e.g. Endoscopy, ECG, Biopsy)</span>
                  ) : (
                    currentProcedures.map((proc, idx) => (
                      <span
                        key={idx}
                        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-indigo-50 text-indigo-800 border border-indigo-200 shadow-2xs"
                      >
                        {proc}
                        <button
                          type="button"
                          onClick={() => handleRemoveProcedure(idx)}
                          className="hover:bg-indigo-200 text-indigo-600 hover:text-indigo-900 rounded-full p-0.5 transition-colors cursor-pointer border-none bg-transparent"
                          title="Remove procedure"
                        >
                          <X size={12} />
                        </button>
                      </span>
                    ))
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* ── Document Uploads Section ───────────────────────────── */}
          <div className="form-item col-span-full border-t border-slate-200 pt-4 mt-2">
            <h3 className="text-sm font-bold text-slate-800 mb-3 flex items-center gap-2">
              <FileText size={16} className="text-teal-600" />
              Doctor Documents & Certificates
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Medical License */}
              <div className="p-3 bg-slate-50 border border-slate-200 rounded-xl flex flex-col gap-2">
                <label className="text-xs font-semibold text-slate-700">Medical License</label>
                <input
                  type="file"
                  accept="image/*,.pdf"
                  onChange={(e) => handleFileUpload(e, 'license')}
                  disabled={uploading.license}
                  className="text-xs text-slate-600 file:mr-2 file:py-1 file:px-2.5 file:rounded-lg file:border-0 file:text-xs file:font-semibold file:bg-teal-50 file:text-teal-700 hover:file:bg-teal-100"
                />
                {uploading.license && <span className="text-xs text-teal-600">Uploading license...</span>}
                {formData.license && (
                  <div className="flex items-center gap-1.5 text-xs text-slate-600">
                    <Check size={13} className="text-emerald-600" />
                    <span className="truncate">{typeof formData.license === 'string' ? formData.license.split('/').pop() : formData.license.name || 'License uploaded'}</span>
                  </div>
                )}
              </div>

              {/* Authorized Signature */}
              <div className="p-3 bg-slate-50 border border-slate-200 rounded-xl flex flex-col gap-2">
                <label className="text-xs font-semibold text-slate-700">Authorized Signature</label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleFileUpload(e, 'authorizedSignature')}
                  disabled={uploading.authorizedSignature}
                  className="text-xs text-slate-600 file:mr-2 file:py-1 file:px-2.5 file:rounded-lg file:border-0 file:text-xs file:font-semibold file:bg-teal-50 file:text-teal-700 hover:file:bg-teal-100"
                />
                {uploading.authorizedSignature && <span className="text-xs text-teal-600">Uploading signature...</span>}
                {formData.authorizedSignature && (
                  <div className="flex items-center gap-1.5 text-xs text-slate-600">
                    <Check size={13} className="text-emerald-600" />
                    <span className="truncate">{typeof formData.authorizedSignature === 'string' ? formData.authorizedSignature.split('/').pop() : formData.authorizedSignature.name || 'Signature uploaded'}</span>
                  </div>
                )}
              </div>

              {/* Commercial License */}
              <div className="p-3 bg-slate-50 border border-slate-200 rounded-xl flex flex-col gap-2">
                <label className="text-xs font-semibold text-slate-700">Commercial / Clinic License</label>
                <input
                  type="file"
                  accept="image/*,.pdf"
                  onChange={(e) => handleFileUpload(e, 'commercialLicense')}
                  disabled={uploading.commercialLicense}
                  className="text-xs text-slate-600 file:mr-2 file:py-1 file:px-2.5 file:rounded-lg file:border-0 file:text-xs file:font-semibold file:bg-teal-50 file:text-teal-700 hover:file:bg-teal-100"
                />
                {uploading.commercialLicense && <span className="text-xs text-teal-600">Uploading license...</span>}
                {formData.commercialLicense && (
                  <div className="flex items-center gap-1.5 text-xs text-slate-600">
                    <Check size={13} className="text-emerald-600" />
                    <span className="truncate">{typeof formData.commercialLicense === 'string' ? formData.commercialLicense.split('/').pop() : formData.commercialLicense.name || 'License uploaded'}</span>
                  </div>
                )}
              </div>

              {/* Civil ID */}
              <div className="p-3 bg-slate-50 border border-slate-200 rounded-xl flex flex-col gap-2">
                <label className="text-xs font-semibold text-slate-700">Civil ID / Identity Document</label>
                <input
                  type="file"
                  accept="image/*,.pdf"
                  onChange={(e) => handleFileUpload(e, 'civilId')}
                  disabled={uploading.civilId}
                  className="text-xs text-slate-600 file:mr-2 file:py-1 file:px-2.5 file:rounded-lg file:border-0 file:text-xs file:font-semibold file:bg-teal-50 file:text-teal-700 hover:file:bg-teal-100"
                />
                {uploading.civilId && <span className="text-xs text-teal-600">Uploading ID...</span>}
                {formData.civilId && (
                  <div className="flex items-center gap-1.5 text-xs text-slate-600">
                    <Check size={13} className="text-emerald-600" />
                    <span className="truncate">{typeof formData.civilId === 'string' ? formData.civilId.split('/').pop() : formData.civilId.name || 'ID uploaded'}</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {subscriptionPanel}
          <button
            className="submit-btn"
            onClick={handleSubmit}
            disabled={
              uploading.vendor_profile_path ||
              uploading.license ||
              uploading.authorizedSignature ||
              uploading.commercialLicense ||
              uploading.civilId ||
              loadingClinics ||
              loadingSpecialities ||
              selectedClinicIds.length === 0
            }
          >
            {editMode ? 'Update Doctor' : 'Create Doctor'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DoctorForm;
